import { fetchUserAttributes } from "aws-amplify/auth";

/** The organization every customer belongs to (see infra auth stack). */
export const USER_ORGANIZATION_ATTRIBUTE = "custom:user_organization";

/** One attribute per project slot: `custom:user_project_1`, `custom:user_project_2`, … */
const USER_PROJECT_SLOT_ATTRIBUTE = /^custom:user_project_(\d+)$/;

/** The alphabet an S3 key segment (`<root>/<org>/<project>/`) can safely hold. */
const IDENTIFIER_PATTERN = /^[a-z0-9-]+$/;

export type CustomerScope = {
  readonly organization: string;
  /** In slot order, without repeats. */
  readonly projects: readonly string[];
};

type UserAttributes = Partial<Record<string, string | undefined>>;

function validIdentifier(
  kind: string,
  value: string,
  attribute: string,
): string {
  if (!IDENTIFIER_PATTERN.test(value)) {
    throw new Error(
      `Your ${kind} '${value}' is invalid: it must contain only ` +
        "lowercase letters, digits, and hyphens. Ask an administrator to fix " +
        `the '${attribute}' attribute on your user.`,
    );
  }
  return value;
}

function slotProjects(attributes: UserAttributes): string[] {
  const slots = Object.entries(attributes)
    .map(([attribute, value]) => ({
      attribute,
      value,
      slot: USER_PROJECT_SLOT_ATTRIBUTE.exec(attribute)?.[1],
    }))
    .filter(
      (entry): entry is { attribute: string; value: string; slot: string } =>
        entry.slot !== undefined && Boolean(entry.value),
    )
    .sort((left, right) => Number(left.slot) - Number(right.slot));
  const projects = slots.map(({ attribute, value }) =>
    validIdentifier("project", value, attribute),
  );
  return [...new Set(projects)];
}

/**
 * Validate the organization and project-slot claims before they are interpolated into
 * S3 key prefixes.
 */
export function customerScopeFromAttributes(
  attributes: UserAttributes,
): CustomerScope {
  const organization = attributes[USER_ORGANIZATION_ATTRIBUTE];
  if (!organization) {
    throw new Error(
      "Your account is not assigned to an organization. Ask an administrator " +
        `to set the '${USER_ORGANIZATION_ATTRIBUTE}' attribute on your user.`,
    );
  }
  validIdentifier("organization", organization, USER_ORGANIZATION_ATTRIBUTE);

  const projects = slotProjects(attributes);
  if (projects.length === 0) {
    throw new Error(
      "Your account is not assigned to any project. Ask an administrator " +
        "to set a 'custom:user_project_<n>' attribute on your user.",
    );
  }
  return { organization, projects };
}

/** Read the signed-in customer's organization and projects from their Cognito attributes. */
export async function resolveCustomerScope(): Promise<CustomerScope> {
  return customerScopeFromAttributes(await fetchUserAttributes());
}
